import { isValidElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react'
import { SearchField } from '../forms/SearchField'
import { Skeleton } from '../feedback/Skeleton'
import { Pagination } from './Pagination'
import { TableSurface } from './TableSurface'
import { resolveComponentVariant } from '../componentContract'
import styles from './Data.module.css'

const INTERACTIVE_TARGET_SELECTOR = [
  'a',
  'button',
  'input',
  'label',
  'select',
  'summary',
  'textarea',
  '[contenteditable]:not([contenteditable="false"])',
  '[role="button"]',
  '[role="checkbox"]',
  '[role="combobox"]',
  '[role="link"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="radio"]',
  '[role="slider"]',
  '[role="spinbutton"]',
  '[role="switch"]',
  '[role="tab"]',
  '[role="textbox"]',
].join(', ')


export function DataTable({
  data = [], columns = [], loading = false, loadingMessage = 'Loading…', emptyMessage = 'No data available',
  searchable = false, searchPlaceholder = 'Search…', paginate = false, pageSize = 10, selectable = false,
  onRowClick, rowAction, selectedKeys, onSelectionChange, keyField = 'id', className = '', density = 'default',
  label = 'Data table', getRowLabel = (row) => row.name || row.title || row[keyField], responsiveColumns = false,
  loadingRows = 5, tableLabel, disabled = false,
}) {
  const safeData = useMemo(() => Array.isArray(data) ? data : [], [data])
  const resolvedDensity = resolveComponentVariant('DataTable density', density, ['compact', 'default'], 'default')
  const [sortColumn, setSortColumn] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc')
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [uncontrolledSelectedKeys, setUncontrolledSelectedKeys] = useState(() => new Set())
  const selectAllCheckboxRef = useRef(null)
  const selectionIsControlled = selectedKeys !== undefined
  const controlledSelectedKeys = useMemo(
    () => selectedKeys instanceof Set ? selectedKeys : new Set(selectedKeys ?? []),
    [selectedKeys],
  )
  const activeSelectedKeys = selectionIsControlled ? controlledSelectedKeys : uncontrolledSelectedKeys

  const processedData = useMemo(() => {
    let result = [...safeData]
    if (searchable && searchTerm) {
      const query = searchTerm.toLocaleLowerCase()
      result = result.filter((row) => columns.some((column) => String(row[column.key] ?? '').toLocaleLowerCase().includes(query)))
    }
    if (sortColumn) {
      const activeColumn = columns.find((column) => column.key === sortColumn)
      const sortValue = activeColumn?.sortValue || ((row) => row[sortColumn])
      result.sort((a, b) => {
        const first = sortValue(a)
        const second = sortValue(b)
        if (first === second) return 0
        if (first == null) return 1
        if (second == null) return -1
        const comparison = typeof first === 'string' && typeof second === 'string'
          ? first.localeCompare(second, undefined, { numeric: true, sensitivity: 'base' })
          : first < second ? -1 : 1
        return sortDirection === 'asc' ? comparison : -comparison
      })
    }
    return result
  }, [columns, safeData, searchTerm, searchable, sortColumn, sortDirection])

  const resolvedPageSize = Number.isFinite(pageSize) && pageSize > 0 ? Math.floor(pageSize) : 10
  const totalPages = Math.max(1, Math.ceil(processedData.length / resolvedPageSize))
  const visiblePage = Math.min(currentPage, totalPages)
  const rows = paginate
    ? processedData.slice((visiblePage - 1) * resolvedPageSize, visiblePage * resolvedPageSize)
    : processedData

  const notifySelection = useCallback((next) => {
    if (!selectionIsControlled) setUncontrolledSelectedKeys(next)
    onSelectionChange?.(safeData.filter((row) => next.has(row[keyField])))
  }, [keyField, onSelectionChange, safeData, selectionIsControlled])

  const customEmpty = isValidElement(emptyMessage)
  const selectedVisibleRows = selectable
    ? rows.reduce(
      (count, row) => count + (activeSelectedKeys.has(row[keyField]) ? 1 : 0),
      0,
    )
    : 0
  const allVisibleSelected = rows.length > 0 && selectedVisibleRows === rows.length
  const someVisibleSelected = selectedVisibleRows > 0 && !allVisibleSelected

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages)
  }, [currentPage, totalPages])

  useEffect(() => {
    if (selectionIsControlled || uncontrolledSelectedKeys.size === 0) return
    const availableKeys = new Set(safeData.map((row) => row[keyField]))
    const next = new Set([...uncontrolledSelectedKeys].filter((key) => availableKeys.has(key)))
    if (next.size === uncontrolledSelectedKeys.size) return
    setUncontrolledSelectedKeys(next)
    onSelectionChange?.(safeData.filter((row) => next.has(row[keyField])))
  }, [keyField, onSelectionChange, safeData, selectionIsControlled, uncontrolledSelectedKeys])

  useEffect(() => {
    if (selectAllCheckboxRef.current) selectAllCheckboxRef.current.indeterminate = someVisibleSelected
  }, [loading, selectable, someVisibleSelected])

  const resolvedLoadingRows = Number.isFinite(loadingRows)
    ? Math.max(1, Math.min(20, Math.floor(loadingRows)))
    : 5

  return (
    <TableSurface
      className={className}
      label={label}
      busy={loading}
      toolbar={searchable ? (
        <SearchField
          className={styles.search}
          value={searchTerm}
          onChange={(value) => { setSearchTerm(value); setCurrentPage(1) }}
          placeholder={searchPlaceholder}
          label={searchPlaceholder}
          inputProps={{ disabled: loading }}
        />
      ) : null}
      footer={!loading && paginate && totalPages > 1 ? <Pagination page={visiblePage} pageCount={totalPages} onPageChange={setCurrentPage} /> : null}
    >
      <span className={styles.loadingAnnouncement} aria-live="polite" aria-atomic="true">{loading ? loadingMessage : ''}</span>
      <table
        className={`${styles.table} ${resolvedDensity === 'compact' ? styles.compact : ''}`}
        data-responsive-columns={responsiveColumns || undefined}
        data-selectable={selectable || undefined}
        aria-busy={loading || undefined}
        aria-label={tableLabel || undefined}
      >
          <thead>
            <tr>
              {selectable ? (
                <th className={styles.checkbox}>
                  <input
                    ref={selectAllCheckboxRef}
                    type="checkbox"
                    aria-label={paginate ? `Select all rows on page ${visiblePage}` : 'Select all rows'}
                    aria-checked={someVisibleSelected ? 'mixed' : allVisibleSelected}
                    checked={!loading && allVisibleSelected}
                    disabled={loading || disabled}
                    onChange={() => {
                      const next = new Set(activeSelectedKeys)
                      rows.forEach((row) => {
                        if (allVisibleSelected) next.delete(row[keyField])
                        else next.add(row[keyField])
                      })
                      notifySelection(next)
                    }}
                  />
                </th>
              ) : null}
              {columns.map((column) => {
                const sorted = sortColumn === column.key
                return (
                  <th
                    key={column.key}
                    className={column.numeric || column.align === 'right'
                      ? `${styles.numeric} ${column.className || ''}`.trim()
                      : column.className || undefined}
                    data-column-priority={column.priority ?? undefined}
                    data-column-sticky={column.sticky === 'start' || column.sticky === 'end' ? column.sticky : undefined}
                    aria-sort={column.sortable ? (sorted ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none') : undefined}
                    scope="col"
                  >
                    {column.sortable ? (
                      <button type="button" className={styles.sortButton} onClick={() => {
                        if (sorted) setSortDirection((current) => current === 'asc' ? 'desc' : 'asc')
                        else { setSortColumn(column.key); setSortDirection('asc') }
                      }}>
                        <span>{column.label}</span>
                        <span className={styles.sortIcon} aria-hidden="true">
                          {sorted
                            ? sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                            : <ChevronsUpDown size={14} />}
                        </span>
                      </button>
                    ) : column.label}
                  </th>
                )
              })}
              {rowAction ? <th scope="col">{rowAction.label || 'Action'}</th> : null}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: resolvedLoadingRows }, (_, rowIndex) => (
                <tr key={`loading-${rowIndex}`} className={styles.skeletonRow} aria-hidden="true">
                  {selectable ? (
                    <td className={styles.checkbox}>
                      <Skeleton show delay={0} minimumDuration={0} announce={false} shape="circle" width="1rem" height="1rem" />
                    </td>
                  ) : null}
                  {columns.map((column, columnIndex) => (
                    <td
                      key={column.key}
                      className={column.numeric || column.align === 'right'
                        ? `${styles.numeric} ${column.className || ''}`.trim()
                        : column.className || undefined}
                      data-column-priority={column.priority ?? undefined}
                      data-column-sticky={column.sticky === 'start' || column.sticky === 'end' ? column.sticky : undefined}
                    >
                      <Skeleton
                        show
                        delay={0}
                        minimumDuration={0}
                        announce={false}
                        shape="text"
                        width={`${58 + ((rowIndex + columnIndex) % 4) * 9}%`}
                      />
                    </td>
                  ))}
                  {rowAction ? (
                    <td>
                      <Skeleton show delay={0} minimumDuration={0} announce={false} shape="text" width="4rem" />
                    </td>
                  ) : null}
                </tr>
              ))
            ) : !rows.length ? (
              <tr><td colSpan={columns.length + (selectable ? 1 : 0) + (rowAction ? 1 : 0)} className={customEmpty ? styles.customEmpty : styles.emptyCell}>{emptyMessage}</td></tr>
            ) : rows.map((row) => {
              const rowKey = row[keyField]
              const rowIsClickable = Boolean(onRowClick && !rowAction)
              return (
                <tr key={rowKey} className={`${styles.row} ${rowIsClickable ? styles.clickable : ''}`.trim()} aria-selected={selectable && activeSelectedKeys.has(rowKey) ? 'true' : undefined} data-selected={selectable && activeSelectedKeys.has(rowKey) ? 'true' : undefined} onClick={(event) => {
                  if (!rowIsClickable) return
                  const interactiveTarget = event.target?.closest?.(INTERACTIVE_TARGET_SELECTOR)
                  if (interactiveTarget && interactiveTarget !== event.currentTarget) return
                  onRowClick(row)
                }} onKeyDown={(event) => {
                  if (event.target !== event.currentTarget) return
                  if (rowIsClickable && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); onRowClick(row) }
                }} tabIndex={rowIsClickable ? 0 : undefined}>
                  {selectable ? (
                    <td className={styles.checkbox} onClick={(event) => event.stopPropagation()}>
                      <input type="checkbox" aria-label={`Select ${getRowLabel(row)}`} checked={activeSelectedKeys.has(rowKey)} disabled={disabled} onChange={() => {
                        const next = new Set(activeSelectedKeys)
                        if (next.has(rowKey)) next.delete(rowKey)
                        else next.add(rowKey)
                        notifySelection(next)
                      }} />
                    </td>
                  ) : null}
                  {columns.map((column) => {
                    // A rowHeader column names its row for assistive technology.
                    const Cell = column.rowHeader ? 'th' : 'td'
                    return (
                      <Cell
                        key={column.key}
                        scope={column.rowHeader ? 'row' : undefined}
                        className={column.numeric || column.align === 'right'
                          ? `${styles.numeric} ${column.className || ''}`.trim()
                          : column.className || undefined}
                        data-column-priority={column.priority ?? undefined}
                        data-column-sticky={column.sticky === 'start' || column.sticky === 'end' ? column.sticky : undefined}
                      >
                        {column.render ? column.render(row) : row[column.key]}
                      </Cell>
                    )
                  })}
                  {rowAction ? <td>{rowAction.render(row)}</td> : null}
                </tr>
              )
            })}
          </tbody>
        </table>
    </TableSurface>
  )
}
